import {
    AssemblyDetailedResponse,
    IAssetResponse,
    IIntegrationResponse,
    IntegrationType,
    IntegrationUploadHistoryCreateRequest,
    IntegrationUploadStatus,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import { IntegrationMappingDetails } from 'pages/Settings/Integrations/types';
import { fetchGatewayWithAuth } from 'utils/fetch';
import { HttpMethod } from 'utils/types';

export type ConfigureIntegrationMappingProps = Readonly<{
    locationId: string;
    method: HttpMethod.Get | HttpMethod.Post | HttpMethod.Delete;
    integrationType: IntegrationType;
    payload?: Partial<IntegrationMappingDetails> & { sd_type: string };
    itemId: string;
    ignoreErrors?: boolean;
}>;

export type UpdateIntegrationMappingProps = Readonly<{
    locationId: string;
    integrationType: IntegrationType;
    payload: Omit<IntegrationMappingDetails, 'sd_id'>;
    item: IAssetResponse | AssemblyDetailedResponse;
}>;

async function requestIntegrationMappingConfiguration({
    locationId,
    method,
    integrationType,
    payload,
    itemId,
    ignoreErrors,
}: ConfigureIntegrationMappingProps): Promise<Response> {
    const url = `v4/${integrationType.toLowerCase()}/asset/${locationId}/${itemId}`;

    return fetchGatewayWithAuth({
        path: url,
        method,
        payload,
        ignoreErrors,
    });
}

export async function getIntegrationMapping({
    locationId,
    integrationType,
    itemId,
}: Pick<
    ConfigureIntegrationMappingProps,
    'locationId' | 'integrationType' | 'itemId'
>): Promise<IntegrationMappingDetails | null> {
    const response = await requestIntegrationMappingConfiguration({
        locationId,
        method: HttpMethod.Get,
        integrationType,
        itemId,
        ignoreErrors: true,
    });

    // Note: 404 is expected due to the APIs architecture, if the mapping does not exist in the s3 json blob.
    // For frontend use, we choose to return null instead of throwing an error.
    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw new Error('Failed to fetch integration mapping');
    }

    const { data } = await response.json();
    return data;
}

export async function updateIntegrationMapping({
    locationId,
    integrationType,
    payload,
    item: asset,
}: UpdateIntegrationMappingProps): Promise<Response> {
    return requestIntegrationMappingConfiguration({
        locationId,
        method: HttpMethod.Post,
        integrationType,
        payload,
        itemId: asset.Id,
    });
}

export async function deleteIntegrationMapping({
    locationId,
    integrationType,
    itemId,
}: Pick<
    ConfigureIntegrationMappingProps,
    'locationId' | 'integrationType' | 'itemId'
>): Promise<Response> {
    return requestIntegrationMappingConfiguration({
        locationId,
        method: HttpMethod.Delete,
        integrationType,
        itemId,
    });
}

export async function uploadIntegrationMappingFile({
    locationId,
    integration,
    file,
}: {
    locationId: string;
    integration: IIntegrationResponse;
    file: File;
}): Promise<Response> {
    const url = `v4/${integration.IntegrationType.toLowerCase()}/assets/${locationId}`;
    const formData = new FormData();
    formData.append('file', file, file.name);

    const response = await fetchGatewayWithAuth({
        path: url,
        method: HttpMethod.Post,
        payload: formData,
    });
    const uploadHistoryPayload = new IntegrationUploadHistoryCreateRequest({
        FileName: file.name,
        Status: response.ok
            ? IntegrationUploadStatus.Completed
            : IntegrationUploadStatus.Failed,
    });
    await sdClient.createIntegrationUploadHistory({
        id: locationId,
        integrationId: integration.Id,
        body: uploadHistoryPayload,
    });
    return response;
}

export const hasIntegrationMapping = (
    integrationMapping: IntegrationMappingDetails
): boolean =>
    integrationMapping.cmms_asset_id !== undefined ||
    integrationMapping.cmms_site !== undefined ||
    integrationMapping.cmms_location !== undefined ||
    integrationMapping.cmms_description !== undefined;
